<template>
  <Loader :value="promises.fetch" class="form-view" :style="style" :data-target="$route.query.target">
    <div class="container view">
      <HeaderComponent :title="header.title" :breadcrumb="header.breadcrumb">
        <template #actions>
          <ButtonComponent
            v-for="(item, index) in actions.secondary"
            :key="index"
            v-bind="item"
            @click="runCallback(item.action, item)"
          />
          <ButtonComponent
            v-if="actions.primary"
            theme="primary"
            :promise="promises.submit"
            :disabled="!validator.passes"
            v-bind="actions.primary"
            @click="submit"
          />
        </template>
      </HeaderComponent>

      <div v-if="steps" class="form-view__steps">
        <template v-for="(step, index) in steps">
          <Icon v-if="index > 0" icon="fa-pro-light:long-arrow-right" />
          <component
            :is="step.tag || 'div'"
            v-bind="step.props"
            :class="['form-view__step', { 'form-view__step--active': step.active }]"
          >
            <span>{{ step.label }}</span>
          </component>
        </template>
      </div>

      <Card tag="form" theme="flat" class="form-view__card" @submit.prevent="submit">
        <template #default>
          <CustomFields v-model="data" :schema="schema" :validator="validator" @action="action" />
        </template>
      </Card>

      <div class="form-view__footer-actions">
        <ButtonComponent v-for="item in actions.footer" v-bind="item" @click="runCallback(item.action, item)" />
      </div>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'

import fetch from '@/fetch'
import hydrateConfig from '@/utils/hydrateConfig'
import Card from '@/components/Card'
import CustomFields, { getValidatorConfig } from '@kvass/custom-fields'
import HeaderComponent from '@/components/Header'
import { ButtonComponent } from 'vue-elder-button'
import { LoaderComponent as Loader } from 'vue-elder-loader'

const defaultData = {
  header: { title: '', breadcrumb: [] },
  actions: { primary: null, secondary: [], footer: [] },
  schema: [],
  style: {},
  steps: [],
}

export default {
  mixins: [
    Validator({
      rules: 'validatorConfig.rules',
      data: 'data',
      labels: 'validatorConfig.labels',
    }),
  ],
  props: {
    target: String,
  },
  watch: {
    target: {
      handler: 'fetch',
      immediate: true,
      deep: true,
    },
  },
  data() {
    return {
      ...structuredClone(defaultData),
      data: {},
      promises: {
        submit: null,
        fetch: null,
      },
    }
  },
  computed: {
    validatorConfig() {
      return getValidatorConfig(this.schema, this.data)
    },
  },
  methods: {
    reset() {
      Object.entries(defaultData).forEach(([k, v]) => (this[k] = structuredClone(v)))
    },
    runCallback(callback, item) {
      if (!callback) return
      return callback(item, this)
    },
    submit() {
      this.promises.submit = fetch(this.target, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.data),
      })
        .then(res => {
          const type = res.headers.get('Content-Type') || ''
          if (type.includes('application/json')) return res.json()
          return
        })
        .then(data => {
          if (!data) return
          this.action(data)
        })
    },
    action(data = {}) {
      if (data instanceof CustomEvent) data = data.detail
      const { type, value } = data
      switch (type) {
        case 'navigate':
          return this.$router.push(value).then(() => this.reset() /* remove loading state on primary button */)
        case 'redirect':
          return (window.location.href = value)
        case 'update':
          return this.setData(value)
        case 'refetch':
          return this.fetch()
      }
    },
    setData(data) {
      data = hydrateConfig(data)
      this.reset()
      this.data = 'data' in data ? { ...data.data, ...this.data } : {}
      ;['header', 'actions', 'schema', 'style', 'steps'].forEach(key => {
        if (!data[key]) return
        this[key] = data[key]
      })
    },
    fetch() {
      if (!this.target) return

      this.reset()
      this.promises.fetch = fetch(this.target, { method: 'GET' })
        .then(res => res.json())
        .then(data => this.setData(data))
    },
  },
  metaInfo() {
    return {
      title: this.header.title || this.$t('loading'),
    }
  },
  created() {
    this.reset()
  },
  components: {
    Card,
    CustomFields,
    ButtonComponent,
    HeaderComponent,
    Loader,
  },
}
</script>

<style lang="scss">
.form-view {
  background-color: $background-grey;

  --form-card-spacing: 0;

  @mixin container {
    margin-inline: auto;
    padding-inline: 2rem;
    max-width: 1500px;
  }

  &__card {
    background-color: var(--form-card-background, white);
  }

  &__card > .card__content {
    padding: var(--form-card-spacing);
  }

  &__footer-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
  }

  &__steps {
    @include container();

    padding: 0 !important;

    display: flex;
    flex-wrap: wrap;
    align-items: center;

    gap: 0.75rem;

    counter-reset: steps;

    margin-bottom: 2rem;

    svg {
      opacity: 0.4;
    }
  }

  &__step {
    text-decoration: none;

    &:is(button, a):hover {
      &:before {
        background-color: var(--k-ui-color-neutral);
      }
    }

    &:before {
      $size: 23px;
      display: inline-block;
      width: $size;
      height: $size;

      text-align: center;

      background-color: var(--k-ui-color-neutral-light);
      border: 1px solid var(--k-ui-color-neutral);
      border-radius: 99px;

      font-family: monospace;
      font-size: 0.7em;
      line-height: $size - 2px;

      margin-right: 0.5rem;

      content: counter(steps);
      counter-increment: steps;
    }

    &--active {
      &:before {
        background-color: var(--primary) !important;
        border-color: var(--primary) !important;
        color: var(--primary-contrast) !important;
      }
    }
  }

  &:has(.kanban) {
    .container,
    .view {
      max-width: initial;
    }

    .header {
      position: initial;
    }

    .form-view__card {
      background-color: transparent;

      & > .card__content {
        padding: 0;
      }
    }

    .form-view__steps {
      padding-inline: 2rem !important;
    }

    .header,
    .project-leads__filter {
      @include container();
    }
  }
}
</style>
